import {
  ChatConversationSchema,
  ZodSyncChatMessagesSchema,
  ZodUpdateChatConversationSchema,
} from "@billingnow/zod-schema";
import { apiClient } from "./client";

export type ChatConversation = ChatConversationSchema;

export interface ChatConversationList {
  pinned: ChatConversation[];
  recent: ChatConversation[];
  /** True when more recent conversations exist than were returned */
  hasMore: boolean;
}

/**
 * List the current user's conversations, split into pinned and recent
 * @param all - Fetch every conversation instead of the 5 most recent
 */
export const getChatConversations = async (all?: boolean): Promise<ChatConversationList> => {
  const response = await apiClient.get<{ conversations: ChatConversationList }>("/api/chat-conversation", {
    params: all ? { all: "true" } : undefined,
  });
  return response.data.conversations;
};

/**
 * Get a conversation with its full message history
 */
export const getChatConversation = async (conversationId: string): Promise<ChatConversation> => {
  const response = await apiClient.get<{ conversation: ChatConversation }>(`/api/chat-conversation/${conversationId}`);
  return response.data.conversation;
};

/**
 * Persist an exchange. Omit conversationId to start a new thread.
 */
export const syncChatMessages = async (data: ZodSyncChatMessagesSchema): Promise<ChatConversation> => {
  const response = await apiClient.post<{ conversation: ChatConversation }>("/api/chat-conversation/messages", data, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data.conversation;
};

/**
 * Rename a conversation or toggle its pinned state
 */
export const updateChatConversation = async (data: ZodUpdateChatConversationSchema): Promise<ChatConversation> => {
  const response = await apiClient.put<{ conversation: ChatConversation }>("/api/chat-conversation", data, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data.conversation;
};

/**
 * Delete a conversation and its messages
 */
export const deleteChatConversation = async (conversationId: string): Promise<ChatConversation> => {
  const response = await apiClient.delete<{ conversation: ChatConversation }>(
    `/api/chat-conversation/${conversationId}`,
  );
  return response.data.conversation;
};
